import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Member = { id: string; name: string; assignee: string };

/**
 * Selector inline del Account Manager — muestra quién es el account manager actual y deja
 * agregarlo/cambiarlo al vuelo (mismo endpoint que ClaimAssignPanel).
 *
 * LEE Y ESCRIBE LA MISMA CASILLA: `claims.account_manager` (columna propia, el sync de Monday no
 * la toca). Antes mostraba `assigned_person` (la persona del board de Payments) pero guardaba
 * `account_manager` — leía una casilla y escribía otra, así que el valor recién guardado "no se
 * veía" y el nombre mostrado podía ser el de otra persona. Ver design/audits/datos-duplicados.
 *
 * Fallback visual: en claims viejos el dato real vive en `assigned_person`; si `account_manager`
 * está vacío se muestra ESE valor atenuado (con title explicando la procedencia). Elegir a
 * alguien lo sella en `account_manager` y deja de ser fallback.
 *
 * Cualquier usuario office+ puede cambiarlo. `initial` prellena mientras carga la asignación.
 */
export function AccountManagerPicker({ claimId, initial, onChanged }: { claimId: string; initial?: string | null; onChanged?: () => void }) {
  const { t } = useI18n();
  const [members, setMembers] = useState<Member[]>([]);
  // `am` = claims.account_manager (la casilla que este selector escribe); `ap` = assigned_person
  // (board de Payments, SOLO respaldo visual para claims viejos sin account manager).
  const [am, setAm] = useState<string | null>(null);
  const [ap, setAp] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ members: Member[] }>('/api/team/members').then((r) => setMembers(r.members)).catch(() => {});
    api.get<{ assigned_person: string | null; account_manager: string | null }>(`/api/claims/${claimId}/assignment`)
      .then((r) => { setAm(r.account_manager); setAp(r.assigned_person); setLoaded(true); })
      .catch(() => {});
  }, [claimId]);

  // Mientras carga, `initial` prellena (los callers pueden mandar assigned_person de listas
  // viejas — es provisional y se reemplaza con lo que diga el endpoint).
  const shown = loaded ? (am?.trim() || ap) : (initial ?? null);
  // Fallback: no hay account_manager pero sí assigned_person → se muestra atenuado.
  const esRespaldo = loaded && !am?.trim() && Boolean(ap?.trim());

  // Valor del select = el `assignee` del miembro que matchea el nombre mostrado (o el texto libre existente).
  const current = (() => {
    const cur = shown?.trim();
    if (!cur) return '';
    const m = members.find((x) => x.assignee.toLowerCase() === cur.toLowerCase() || x.name.toLowerCase() === cur.toLowerCase());
    return m ? m.assignee : cur;
  })();

  async function save(person: string) {
    setBusy(true);
    try { await api.post(`/api/claims/${claimId}/account-manager`, { person: person || null }); setAm(person || null); onChanged?.(); }
    catch { /* silencioso */ } finally { setBusy(false); }
  }

  const sel: React.CSSProperties = {
    padding: '4px 6px', border: '1px solid var(--line)', borderRadius: 6, font: 'inherit', fontSize: 'var(--fs-xs)',
    background: 'var(--card)', color: 'var(--ink)', maxWidth: 180,
    // Atenuado cuando lo mostrado es el respaldo (assigned_person), no el account manager real.
    ...(esRespaldo ? { opacity: 0.6, fontStyle: 'italic' } : {}),
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-xs)' }}>
      <span className="muted">{t('ct.accountManager')}: </span>
      <select style={sel} disabled={busy} value={current} title={esRespaldo ? t('amp.fallbackHint') : undefined} onChange={(e) => save(e.target.value)}>
        <option value="">— {t('asg.none')} —</option>
        {current && !members.some((m) => m.assignee === current) && <option value={current}>{shown}</option>}
        {members.map((m) => <option key={m.id} value={m.assignee}>{m.name}</option>)}
      </select>
    </div>
  );
}
